import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, ShoppingBag } from "lucide-react";
import api from "../lib/api";

const styles = {
  page: {
    maxWidth: 860,
    margin: "0 auto",
    padding: "16px 14px 28px",
    background: "#f6f7fb",
    minHeight: "100vh",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
  },
  backButton: {
    cursor: "pointer",
    fontSize: "20px",
    background: "rgba(255,255,255,0.2)",
    padding: "12px",
    borderRadius: "50%",
    transition: "background 0.2s ease",
    color: "#333",
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 800,
    color: "#0f172a",
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: 13,
    color: "#667085",
  },
  loading: {
    textAlign: "center",
    color: "#667085",
    marginTop: 70,
  },
  emptyWrap: {
    marginTop: 24,
    borderRadius: 18,
    border: "1px solid #e6eaf2",
    background: "#fff",
    padding: "34px 18px",
    textAlign: "center",
  },
  emptyText: {
    margin: "10px 0 16px",
    color: "#6b7280",
    fontSize: 14,
  },
  primaryButton: {
    border: "none",
    borderRadius: 12,
    background: "var(--primary)",
    color: "#fff",
    padding: "10px 16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  ordersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
    gap: 12,
    marginTop: 18,
  },
  orderCard: {
    borderRadius: 16,
    border: "1px solid #e8ebf2",
    background: "#fff",
    padding: 14,
    boxShadow: "0 2px 10px rgba(15, 23, 42, 0.05)",
  },
  orderHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 6,
  },
  orderId: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  orderDate: {
    margin: 0,
    color: "#6b7280",
    fontSize: 12,
  },
  itemsWrap: {
    marginTop: 10,
    borderTop: "1px solid #edf0f6",
    paddingTop: 10,
  },
  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  itemName: {
    fontSize: 13,
    color: "#111827",
  },
  itemMeta: {
    color: "#6b7280",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  footer: {
    marginTop: 10,
    borderTop: "1px solid #edf0f6",
    paddingTop: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 700,
    color: "#111827",
  },
  metaBlock: {
    marginTop: 8,
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 1.45,
  },
  badge: {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    border: "1px solid transparent",
  },
  pendingBadge: {
    color: "#92400e",
    background: "#fff7ed",
    borderColor: "#fed7aa",
  },
  processingBadge: {
    color: "#1d4ed8",
    background: "#eff6ff",
    borderColor: "#bfdbfe",
  },
  completedBadge: {
    color: "#166534",
    background: "#f0fdf4",
    borderColor: "#bbf7d0",
  },
  cancelledBadge: {
    color: "#991b1b",
    background: "#fef2f2",
    borderColor: "#fecaca",
  },
};

function getStatusStyle(status) {
  if (status === "Pending") return { ...styles.badge, ...styles.pendingBadge };
  if (status === "Processing") return { ...styles.badge, ...styles.processingBadge };
  if (status === "Completed") return { ...styles.badge, ...styles.completedBadge };
  return { ...styles.badge, ...styles.cancelledBadge };
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await api.get("/api/orders/", );
        setOrders(res.data || []);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  if (loading) return <div style={styles.loading}>Loading your orders...</div>;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <i
          className="fi fi-br-angle-left"
          onClick={() => navigate("/home")}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={styles.backButton}
        ></i>
        <div>
          <h2 style={styles.title}>My Orders</h2>
          <p style={styles.subtitle}>Track and review your pharmacy purchases</p>
        </div>
      </div>

      {orders.length === 0 ? (
        <div style={styles.emptyWrap}>
          <ShoppingBag size={48} color="#9aa2b1" />
          <p style={styles.emptyText}>You haven’t placed any orders yet.</p>
          <button type="button" style={styles.primaryButton} onClick={() => navigate("/epharmacy")}>
            Start Shopping
          </button>
        </div>
      ) : (
        <div style={styles.ordersGrid}>
          {orders.map((order) => (
            <div key={order._id} style={styles.orderCard}>
              <div style={styles.orderHead}>
                <h5 style={styles.orderId}>
                  <Package size={16} /> Order #{order._id.slice(-6).toUpperCase()}
                </h5>
                <span style={getStatusStyle(order.status)}>
                    {order.status}
                  </span>
              </div>

              <p style={styles.orderDate}>{new Date(order.createdAt).toLocaleString()}</p>

              <div style={styles.itemsWrap}>
                  {order.items.map((item) => (
                    <div
                      key={item._id}
                      style={styles.itemRow}
                    >
                      <span style={styles.itemName}>{item.medicine?.name || "Medicine"}</span>
                      <small style={styles.itemMeta}>
                        x{item.quantity} • ${item.price}
                      </small>
                    </div>
                  ))}
              </div>

              <div style={styles.footer}>
                  <strong>Total:</strong>
                  <span>${order.totalPrice.toFixed(2)}</span>
              </div>

              <div style={styles.metaBlock}>
                  <small>
                    Pharmacy: {order.pharmacy?.name || order.pharmacy || "N/A"}
                  </small>
                  <br />
                  <small>
                    Payment: {order.paymentMethod}
                  </small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
